import React, { useState } from 'react';
import { Layers, ArrowLeft, ArrowRightLeft } from 'lucide-react';
import { AppView } from '../../types';

interface UnitConverterCalculatorProps {
  onNavigateToView: (view: AppView) => void;
}

export const UnitConverterCalculator: React.FC<UnitConverterCalculatorProps> = ({ onNavigateToView }) => {
  const [dimension, setDimension] = useState<'length' | 'mass' | 'volume' | 'temp'>('length');
  const [inputValue, setInputValue] = useState<string>('100');
  const [fromUnit, setFromUnit] = useState<string>('km');
  const [toUnit, setToUnit] = useState<string>('mi');

  // Conversion logic
  const val = parseFloat(inputValue) || 0;
  let result = 0;

  if (dimension === 'length') {
    // base: meters
    let inMeters = val;
    if (fromUnit === 'km') inMeters = val * 1000;
    else if (fromUnit === 'cm') inMeters = val / 100;
    else if (fromUnit === 'mi') inMeters = val * 1609.344;
    else if (fromUnit === 'ft') inMeters = val * 0.3048;
    else if (fromUnit === 'in') inMeters = val * 0.0254;

    if (toUnit === 'km') result = inMeters / 1000;
    else if (toUnit === 'm') result = inMeters;
    else if (toUnit === 'cm') result = inMeters * 100;
    else if (toUnit === 'mi') result = inMeters / 1609.344;
    else if (toUnit === 'ft') result = inMeters / 0.3048;
    else if (toUnit === 'in') result = inMeters / 0.0254;
  } else if (dimension === 'mass') {
    // base: kg
    let inKg = val;
    if (fromUnit === 'g') inKg = val / 1000;
    else if (fromUnit === 'mg') inKg = val / 1000000;
    else if (fromUnit === 'lb') inKg = val * 0.45359237;
    else if (fromUnit === 'oz') inKg = val * 0.02834952;

    if (toUnit === 'kg') result = inKg;
    else if (toUnit === 'g') result = inKg * 1000;
    else if (toUnit === 'lb') result = inKg / 0.45359237;
    else if (toUnit === 'oz') result = inKg / 0.02834952;
  } else if (dimension === 'temp') {
    // affine conversion
    let inC = val;
    if (fromUnit === 'F') inC = (val - 32) * (5 / 9);
    else if (fromUnit === 'K') inC = val - 273.15;

    if (toUnit === 'C') result = inC;
    else if (toUnit === 'F') result = inC * (9 / 5) + 32;
    else if (toUnit === 'K') result = inC + 273.15;
  }

  const handleSwap = () => {
    const temp = fromUnit;
    setFromUnit(toUnit);
    setToUnit(temp);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                YamooPay Universal Unit Converter (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Convertisseur d'Unités Universel (SI & Standard)
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'length', label: 'Longueur' },
            { id: 'mass', label: 'Masse / Poids' },
            { id: 'temp', label: 'Température' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setDimension(tab.id as any);
                if (tab.id === 'length') { setFromUnit('km'); setToUnit('mi'); }
                if (tab.id === 'mass') { setFromUnit('kg'); setToUnit('lb'); }
                if (tab.id === 'temp') { setFromUnit('C'); setToUnit('F'); }
              }}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                dimension === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
            <h3 className="text-base font-bold text-[#0F172A] mb-4">Paramètres de Conversion</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1">Valeur</label>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1">De</label>
                <select
                  value={fromUnit}
                  onChange={(e) => setFromUnit(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {dimension === 'length' && (
                    <>
                      <option value="km">Kilomètre (km)</option>
                      <option value="m">Mètre (m)</option>
                      <option value="cm">Centimètre (cm)</option>
                      <option value="mi">Mile (mi)</option>
                      <option value="ft">Pied (ft)</option>
                      <option value="in">Pouce (in)</option>
                    </>
                  )}
                  {dimension === 'mass' && (
                    <>
                      <option value="kg">Kilogramme (kg)</option>
                      <option value="g">Gramme (g)</option>
                      <option value="lb">Livre (lb)</option>
                      <option value="oz">Ounce (oz)</option>
                    </>
                  )}
                  {dimension === 'temp' && (
                    <>
                      <option value="C">Celsius (°C)</option>
                      <option value="F">Fahrenheit (°F)</option>
                      <option value="K">Kelvin (K)</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#475569] mb-1">Vers</label>
                <select
                  value={toUnit}
                  onChange={(e) => setToUnit(e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  {dimension === 'length' && (
                    <>
                      <option value="km">Kilomètre (km)</option>
                      <option value="m">Mètre (m)</option>
                      <option value="cm">Centimètre (cm)</option>
                      <option value="mi">Mile (mi)</option>
                      <option value="ft">Pied (ft)</option>
                      <option value="in">Pouce (in)</option>
                    </>
                  )}
                  {dimension === 'mass' && (
                    <>
                      <option value="kg">Kilogramme (kg)</option>
                      <option value="g">Gramme (g)</option>
                      <option value="lb">Livre (lb)</option>
                      <option value="oz">Ounce (oz)</option>
                    </>
                  )}
                  {dimension === 'temp' && (
                    <>
                      <option value="C">Celsius (°C)</option>
                      <option value="F">Fahrenheit (°F)</option>
                      <option value="K">Kelvin (K)</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <button
              onClick={handleSwap}
              className="px-4 py-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] text-[#0F172A] rounded-xl text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Inverser les unités
            </button>
          </div>

          <div className="bg-gradient-to-br from-rose-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
            <div>
              <div className="text-xs font-bold text-rose-300 uppercase tracking-wider mb-2">Résultat Converti</div>
              <div className="text-4xl font-extrabold tracking-tight mt-1">
                {result.toLocaleString('en-US', { maximumFractionDigits: 4 })} {toUnit}
              </div>
              <div className="text-xs text-rose-200 mt-2">
                {val} {fromUnit} = {result.toFixed(4)} {toUnit}
              </div>
            </div>
            <div className="mt-8 pt-4 border-t border-white/10 text-xs text-slate-300">
              Conversions normalisées via le Système International (SI).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
